import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Shield,
  Globe,
  FileSearch,
  Mail,
  AppWindow,
  Cpu,
  Users,
  CheckCircle,
  ArrowRight,
  Heart,
  Lightbulb,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import MarketingPageLayout from '../components/marketing/MarketingPageLayout';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Core values data
const coreValues = [
  {
    icon: Shield,
    title: 'Cyber Security First',
    description: 'Every decision we make prioritizes the security and protection of our customers. No compromises, no shortcuts.'
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'We continuously push the boundaries of AI and machine learning to stay ahead of evolving cyber threats.'
  },
  {
    icon: Users,
    title: 'Accessibility',
    description: 'Enterprise-grade cyber security protection should be available to businesses of all sizes, not just Fortune 500 companies.'
  },
  {
    icon: Heart,
    title: 'Customer Success',
    description: 'Your security is our success. We measure ourselves by how well we protect your organization from cyber threats.'
  },
];

// Protection layers
const protectionLayers = [
  {
    icon: Globe,
    title: 'DNS Cyber Security',
    description: 'Block malicious domains at the network edge before threats can reach your endpoints.',
    color: 'blue'
  },
  {
    icon: Mail,
    title: 'Email Security',
    description: 'AI-powered phishing detection and attachment scanning for complete email protection.',
    color: 'red'
  },
  {
    icon: FileSearch,
    title: 'File Analysis',
    description: 'Real-time file scanning and sandboxing to catch malware before it executes.',
    color: 'orange'
  },
  {
    icon: AppWindow,
    title: 'App Monitoring',
    description: 'Behavioral analysis of applications to detect anomalies and prevent unauthorized actions.',
    color: 'purple'
  },
];

// Stats
const stats = [
  { value: '500+', label: 'Protected Organizations' },
  { value: '99.9%', label: 'Threat Detection Rate' },
  { value: '24/7', label: 'Cyber Security Monitoring' },
  { value: '<100ms', label: 'Average Response Time' },
];

// Hero Section
const HeroSection = () => (
  <section aria-labelledby="about-hero-heading" className="pt-32 pb-20 relative overflow-hidden">
    <div className="absolute inset-0" aria-hidden="true">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-3xl" />
    </div>
    
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.header
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        className="text-center max-w-4xl mx-auto"
      >
        <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 mb-6">
          About Clear Defense AI
        </Badge>
        <h1 id="about-hero-heading" className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
          Clean. Strong.{' '}
          <span className="bg-gradient-to-r from-blue-400 to-cyan-400 text-transparent bg-clip-text">
            Enterprise-Grade Cyber Security.
          </span>
        </h1>
        <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto">
          We&apos;re building the future of cyber security—one where every business, 
          regardless of size, has access to world-class protection against cyber threats.
        </p>
      </motion.header>
    </div>
  </section>
);

// Mission Section
const MissionSection = () => (
  <section aria-labelledby="mission-heading" className="py-20 bg-slate-900/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.article
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 mb-4">
            Our Mission
          </Badge>
          <h2 id="mission-heading" className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
            Democratizing Enterprise Cyber Security
          </h2>
          <div className="space-y-4 text-slate-400">
            <p>
              Clear Defense AI is a Toronto-based cyber security company built for a new era of digital threats. 
              We believe that small and mid-sized businesses deserve the same level of cyber security protection as global 
              enterprises—without the cost, complexity, or heavy IT burden.
            </p>
            <p>
              Founded by cyber security professionals who witnessed firsthand how SMBs struggle to defend against 
              sophisticated cyberattacks, we set out to create a solution that levels the playing field. 
              Our platform brings Fortune 500-level cyber security protection to organizations of every size.
            </p>
            <p>
              Today, we protect over 500 businesses across North America and beyond, blocking millions of 
              cyber threats every month while our customers focus on what they do best—growing their businesses.
            </p>
          </div>
        </motion.article>
        
        <motion.aside
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="grid grid-cols-2 gap-4"
          aria-label="Cyber security platform statistics"
        >
          {stats.map((stat, index) => (
            <Card key={index} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <p className="text-3xl font-heading font-bold text-white mb-1">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </motion.aside>
      </div>
    </div>
  </section>
);

// Platform Section
const PlatformSection = () => (
  <section aria-labelledby="platform-heading" className="py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
        className="text-center mb-16"
      >
        <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 mb-4">
          Our Cyber Security Platform
        </Badge>
        <h2 id="platform-heading" className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
          One Platform. Four Layers. Complete Cyber Security Protection.
        </h2>
        <p className="text-lg text-slate-400 max-w-3xl mx-auto">
          Our cyber security platform unifies DNS protection, email security, file analysis, and application monitoring 
          into a single AI-powered defense layer. Instead of relying on multiple disconnected tools, 
          Clear Defense AI gives your organization one seamless system that detects, blocks, and mitigates 
          cyber threats in real time across every endpoint.
        </p>
      </motion.header>
      
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        role="list"
        aria-label="Cyber security protection layers"
      >
        {protectionLayers.map((layer, index) => (
          <motion.article key={index} variants={fadeInUp} role="listitem">
            <Card className="bg-slate-900/50 border-slate-800 h-full hover:border-slate-700 transition-colors">
              <CardContent className="p-6">
                <div className={`w-12 h-12 rounded-xl bg-${layer.color}-500/10 flex items-center justify-center mb-4`}>
                  <layer.icon className={`w-6 h-6 text-${layer.color}-500`} aria-hidden="true" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">{layer.title}</h3>
                <p className="text-slate-400 text-sm">{layer.description}</p>
              </CardContent>
            </Card>
          </motion.article>
        ))}
      </motion.div>
    </div>
  </section>
);

// AI Section
const AISection = () => (
  <section aria-labelledby="ai-heading" className="py-20 bg-slate-900/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.figure
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="order-2 lg:order-1"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl" aria-hidden="true" />
            <Card className="relative bg-slate-900 border-slate-800">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Cpu className="w-7 h-7 text-blue-500" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-xl text-white">AI Cyber Security Threat Engine</h3>
                    <p className="text-slate-400 text-sm">Powered by GPT-5.1</p>
                  </div>
                </div>
                <ul className="space-y-4" aria-label="AI threat engine capabilities">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-slate-300">Analyzes millions of cyber threat signals daily</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-slate-300">Human-readable cyber security threat explanations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-slate-300">Automated response recommendations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-slate-300">Continuous learning from global cyber threat data</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </motion.figure>
        
        <motion.article
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="order-1 lg:order-2"
        >
          <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 mb-4">
            Artificial Intelligence Cyber Security
          </Badge>
          <h2 id="ai-heading" className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
            Cyber Security Intelligence That Never Sleeps
          </h2>
          <div className="space-y-4 text-slate-400">
            <p>
              Designed for rapid deployment and effortless management, Clear Defense AI continuously 
              learns from millions of cyber threat signals to stay ahead of evolving cyberattacks.
            </p>
            <p>
              Our proprietary AI cyber security engine doesn&apos;t just detect threats—it understands them. Using advanced 
              natural language processing, it provides your team with clear, actionable insights about 
              each security event, eliminating the noise and highlighting what truly matters.
            </p>
            <p>
              With automated enforcement and intelligent risk scoring, your team can operate with 
              confidence knowing your environment is secured by enterprise-grade cyber security protection at every layer.
            </p>
          </div>
        </motion.article>
      </div>
    </div>
  </section>
);

// Values Section
const ValuesSection = () => (
  <section aria-labelledby="values-heading" className="py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
        className="text-center mb-16"
      >
        <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 mb-4">
          Our Values
        </Badge>
        <h2 id="values-heading" className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
          What Drives Our Cyber Security Mission
        </h2>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
          Our core values guide every decision we make and every cyber security feature we build.
        </p>
      </motion.header>
      
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        role="list"
        aria-label="Company core values"
      >
        {coreValues.map((value, index) => (
          <motion.article key={index} variants={fadeInUp} role="listitem">
            <Card className="bg-slate-900/50 border-slate-800 h-full hover:border-slate-700 transition-colors">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-blue-500" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">{value.title}</h3>
                <p className="text-slate-400 text-sm">{value.description}</p>
              </CardContent>
            </Card>
          </motion.article>
        ))}
      </motion.div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section aria-labelledby="about-cta-heading" className="py-24 relative overflow-hidden">
    <div className="absolute inset-0" aria-hidden="true">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-3xl" />
    </div>
    
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeIn}
      className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
    >
      <h2 id="about-cta-heading" className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
        Ready to Experience the Cyber Security Difference?
      </h2>
      <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
        Join hundreds of organizations that trust Clear Defense AI cyber security platform to protect their business. 
        Start your free trial today—no credit card required.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/signup">
          <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8">
            Start Free Trial
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
        <Link to="/#pricing">
          <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 text-lg px-8">
            View Cyber Security Pricing
          </Button>
        </Link>
      </div>
    </motion.div>
  </section>
);

// Main About Us Page Component
export default function AboutUs() {
  return (
    <MarketingPageLayout>
      <main>
        <HeroSection />
        <MissionSection />
        <PlatformSection />
        <AISection />
        <ValuesSection />
        <CTASection />
      </main>
    </MarketingPageLayout>
  );
}
